import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { ParametersField } from "@/components/doc/right-sidebar/active-node-style/fields/ParametersField";
import { Sidebar } from "@/components/sidebar";
import { getSuggestionNode } from "@/components/suggestions/providers/utils";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { trackEvent } from "@/utils/trackEvent";

import { GraphPreview } from "../../graph-preview/GraphPreview";
import { NodeValueSuggestionsField } from "../fields/NodeValueSuggestionsField";
import { SymbolFieldDropdown } from "../symbol/SymbolFieldDropdown";
import { SymbolIndexFields } from "../symbol/SymbolIndexFields";

import { FunctionBracketsSelector } from "./FunctionBracketsSelector";

import type { SingleSuggestion } from "@/components/suggestions/types";

type Props = {
  node: FunctionNodeModel;
};

export const FunctionStyle = observer(function FunctionStyle({ node }: Props) {
  const { doc, symbolNode } = node;
  const t = useTranslations("sidebar.activeNode");

  const handleSuggestionSelect = (suggestion: SingleSuggestion) => {
    doc.transact(() => {
      const [newNode] = getSuggestionNode(suggestion, doc) ?? [];

      if (newNode) {
        if (!newNode.is(FunctionNodeModel)) {
          doc.removeNode(newNode);
          return;
        }

        const { parameters } = node;

        newNode.parameters.forEach((param) => doc.removeNode(param));
        newNode.appendChildren(parameters);

        if (symbolNode && symbolNode.labelNodes.length > 0) {
          newNode.symbolNode?.setChildren(symbolNode.labelNodes);
        }

        node.replaceWith(newNode);
      }
    }, "FunctionStyle:suggestionSelect");
    doc.focus();
  };

  return (
    <div data-panel="function_style" className="px-[5px]">
      {symbolNode && (
        <>
          <div className="flex">
            <NodeValueSuggestionsField
              doc={doc}
              inputProps={{
                id: `value-${symbolNode.id}`,
                value: symbolNode.value,
                action: node.name && (
                  <span className="text-gray-500 text-xs inline-block w-full py-[1px] truncate pr-1">
                    {node.name}
                  </span>
                ),
                labelProps: {
                  label: undefined,
                },
              }}
              suggestionsProps={{
                onSelect: handleSuggestionSelect,
                accept: ["function", "serviceFunction"],
              }}
            />
            <div className="h-[28px] ml-1 flex-shrink-0">
              <SymbolFieldDropdown node={symbolNode} className="h-full" />
            </div>
          </div>
          {symbolNode.labelNodes.length > 0 && (
            <SymbolIndexFields
              node={symbolNode}
              contextId="label-field"
              className="flex-1 min-w-0 flex-wrap"
              label={t("fields.parameters")}
            />
          )}
        </>
      )}
      <ParametersField node={node} className="mb-[5px]" />
      <Sidebar.Delimiter className="my-3" />
      <FunctionBracketsSelector node={node} />
      <GraphPreview
        doc={doc}
        expressions={[[node]]}
        onInsert={() => trackEvent("graph_insert", { source: "function" })}
      />
    </div>
  );
});
